/**
 * Tabular Data Visualizer Tool
 *
 * Renders CSV/TSV/Pipe as a scrollable, editable table with format detection,
 * conversion, sorting, and row/column deletion.
 */

import * as Papa from 'papaparse';
import React from 'react';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { IVisualizerTool, VisualizerRenderContext } from '@/interfaces/ITool';
import { detectTabularFormat,formatToDelimiter } from '@/lib/tabularFormatDetection';

import { type SortDirection, type TabularFormat,TabularTableView } from './TabularTableView';
import { type UndoKind,useUndoStack } from './useUndoStack';

/** Resolve the working delimiter — explicit format > auto-detection > fallback */
function resolveWorkingDelimiter(fmt: string, input: string): string {
  const explicit = formatToDelimiter(fmt);
  if (explicit) return explicit;
  const detected = detectTabularFormat(input);
  return detected?.delimiter ?? ',';
}

/** Parse input into rows using the resolved delimiter (no trimming — raw data) */
function parseRows(input: string, delimiter: string): { rows: string[][]; detectedDelimiter: string } {
  if (!input.trim()) return { rows: [], detectedDelimiter: delimiter };
  const result = Papa.parse<string[]>(input, {
    delimiter,
    skipEmptyLines: false,
    dynamicTyping: false,
  });
  return {
    rows: (result.data ?? []) as string[][],
    detectedDelimiter: result.meta?.delimiter ?? delimiter,
  };
}

class TabularDataVisualizerToolImpl implements IVisualizerTool {
  readonly id = 'tabular-data-visualizer';
  readonly name = 'Tabular Table View';
  readonly description = 'Display CSV/TSV data as an interactive table';
  readonly version = '1.0.0';
  readonly type = 'visualizer' as const;
  readonly category = 'Visualizers';
  readonly editable = true;
  readonly keywords = ['csv', 'tsv', 'table', 'tabular', 'visualize', 'visualizer'];

  readonly supportedInputTypes: ISemanticType[] = [SEMANTIC_TYPES.CSV, SEMANTIC_TYPES.TSV, SEMANTIC_TYPES.PIPE];

  readonly outputType = SEMANTIC_TYPES.CSV;

  supportsInput(): boolean {
    return true;
  }

  readonly attributes = [
    {
      name: 'format',
      type: 'select' as const,
      label: 'Format',
      defaultValue: 'auto',
      options: ['auto', 'csv', 'tsv', 'pipe', 'semicolon'],
      description: 'Parse as CSV, TSV, Pipe-delimited, or auto-detect from the first line',
    },
    {
      name: 'hasHeader',
      type: 'boolean' as const,
      label: 'Has Header Row',
      defaultValue: true,
      description: 'Treat the first row as headers',
    },
    {
      name: 'trimWhitespace',
      type: 'boolean' as const,
      label: 'Trim Whitespace',
      defaultValue: true,
      description: 'Trim whitespace around cell values',
    },
  ];

  render(context: VisualizerRenderContext): React.ReactElement {
    const { input, config, editMode, onEdit, onConfigChange } = context;

    const fmt = (config.format as string) ?? 'auto';
    const hasHdr = (config.hasHeader as boolean) ?? true;

    const { recordChange, undo, redo, canUndo, canRedo } = useUndoStack({
      value: input,
      onApply: onEdit,
      maxDepth: 50,
    });

    const applyChange = React.useCallback((kind: UndoKind, nextValue: string) => {
      if (!onEdit || nextValue === input) return;
      recordChange({ kind, before: input, after: nextValue });
      onEdit(nextValue);
    }, [input, onEdit, recordChange]);

    /** All mutation helpers share this parse→mutate→serialize pattern */
    const getDelimiter = () => resolveWorkingDelimiter(fmt, input);

    // ── Cell edit ─────────────────────────────────────────
    const handleCellEdit = React.useCallback((rowIndex: number, colIndex: number, newValue: string) => {
      const delimiter = getDelimiter();

      if (!input.trim()) {
        const r: string[][] = hasHdr ? [['Column 1'], ['']] : [['']];
        const actualRow = hasHdr ? rowIndex + 1 : rowIndex;
        if (r[actualRow]) r[actualRow][colIndex] = newValue;
        applyChange('cell', Papa.unparse(r, { delimiter }));
        return;
      }

      const { rows } = parseRows(input, delimiter);
      const actualRow = hasHdr ? rowIndex + 1 : rowIndex;
      if (rows[actualRow]) {
        rows[actualRow][colIndex] = newValue;
      }
      applyChange('cell', Papa.unparse(rows, { delimiter }));
    }, [input, fmt, hasHdr, applyChange]);

    // ── Add row ───────────────────────────────────────────
    const handleAddRow = React.useCallback(() => {
      const delimiter = getDelimiter();

      if (!input.trim()) {
        applyChange('row', Papa.unparse(hasHdr ? [['Column 1'], ['']] : [['']], { delimiter }));
        return;
      }
      const { rows } = parseRows(input, delimiter);
      const colCount = Math.max(...rows.map((r) => r.length), 1);
      rows.push(Array(colCount).fill(''));
      applyChange('row', Papa.unparse(rows, { delimiter }));
    }, [input, fmt, hasHdr, applyChange]);

    // ── Add column ────────────────────────────────────────
    const handleAddCol = React.useCallback(() => {
      const delimiter = getDelimiter();

      if (!input.trim()) {
        applyChange('column', Papa.unparse(hasHdr ? [['Column 1', 'Column 2'], ['', '']] : [['', '']], { delimiter }));
        return;
      }
      const { rows } = parseRows(input, delimiter);
      for (let i = 0; i < rows.length; i++) {
        rows[i].push(i === 0 && hasHdr ? `Column ${rows[0].length + 1}` : '');
      }
      applyChange('column', Papa.unparse(rows, { delimiter }));
    }, [input, fmt, hasHdr, applyChange]);

    // ── Delete row ────────────────────────────────────────
    const handleDeleteRow = React.useCallback((rowIndex: number) => {
      const delimiter = getDelimiter();
      const { rows } = parseRows(input, delimiter);
      const actualRow = hasHdr ? rowIndex + 1 : rowIndex;

      // Don't delete the last data row
      const dataRowCount = hasHdr ? rows.length - 1 : rows.length;
      if (dataRowCount <= 1) return;

      if (actualRow >= 0 && actualRow < rows.length) {
        rows.splice(actualRow, 1);
      }
      applyChange('row', Papa.unparse(rows, { delimiter }));
    }, [input, fmt, hasHdr, applyChange]);

    // ── Delete column ─────────────────────────────────────
    const handleDeleteCol = React.useCallback((colIndex: number) => {
      const delimiter = getDelimiter();
      const { rows } = parseRows(input, delimiter);

      // Don't delete the last column
      const colCount = Math.max(...rows.map((r) => r.length), 0);
      if (colCount <= 1) return;

      for (const row of rows) {
        if (colIndex < row.length) {
          row.splice(colIndex, 1);
        }
      }
      applyChange('column', Papa.unparse(rows, { delimiter }));
    }, [input, fmt, hasHdr, applyChange]);

    // ── Convert format ────────────────────────────────────
    const handleConvertFormat = React.useCallback((targetDelimiter: string) => {
      const currentDelimiter = getDelimiter();
      const { rows } = parseRows(input, currentDelimiter);

      if (rows.length === 0) return;

      applyChange('convert', Papa.unparse(rows, { delimiter: targetDelimiter }));

      // Also update the format config so the badge reflects the new format
      if (onConfigChange) {
        const newFormat = targetDelimiter === '\t' ? 'tsv' : targetDelimiter === '|' ? 'pipe' : targetDelimiter === ';' ? 'semicolon' : 'csv';
        onConfigChange({ ...config, format: newFormat });
      }
    }, [input, fmt, config, onConfigChange, applyChange]);

    // ── Sort (edit mode — materializes) ───────────────────
    const handleSort = React.useCallback((colIndex: number, direction: SortDirection) => {
      if (direction === 'none') return;
      const delimiter = getDelimiter();
      const { rows } = parseRows(input, delimiter);

      if (rows.length < 2) return;

      const headerRows = hasHdr ? [rows[0]] : [];
      const dataRowsRaw = hasHdr ? rows.slice(1) : rows;

      dataRowsRaw.sort((a, b) => {
        const aVal = String(a[colIndex] ?? '');
        const bVal = String(b[colIndex] ?? '');
        const cmp = aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
        return direction === 'desc' ? -cmp : cmp;
      });

      applyChange('sort', Papa.unparse([...headerRows, ...dataRowsRaw], { delimiter }));
    }, [input, fmt, hasHdr, applyChange]);

    // ── Wire format change through to config ──────────────
    const handleFormatChange = React.useCallback((newFormat: string) => {
      if (onConfigChange) {
        onConfigChange({ ...config, format: newFormat });
      }
    }, [config, onConfigChange]);

    const handleHeaderEdit = React.useCallback((colIndex: number, newValue: string) => {
      if (!hasHdr) return;
      const delimiter = getDelimiter();

      if (!input.trim()) {
        const colCount = Math.max(colIndex + 1, 1);
        const headerRow = Array.from({ length: colCount }, (_, i) => `Column ${i + 1}`);
        headerRow[colIndex] = newValue;
        const rows = [headerRow, Array(colCount).fill('')];
        applyChange('header', Papa.unparse(rows, { delimiter }));
        return;
      }

      const { rows } = parseRows(input, delimiter);
      if (rows.length === 0) rows.push([]);
      if (!rows[0]) rows[0] = [];
      while (rows[0].length <= colIndex) {
        rows[0].push(`Column ${rows[0].length + 1}`);
      }
      rows[0][colIndex] = newValue;
      applyChange('header', Papa.unparse(rows, { delimiter }));
    }, [input, fmt, hasHdr, applyChange]);

    const handlePasteCells = React.useCallback((rowIndex: number, colIndex: number, values: string[][]) => {
      if (values.length === 0) return;
      const delimiter = getDelimiter();
      const { rows } = parseRows(input, delimiter);
      const workingRows = rows.length ? rows : [];
      const maxPasteCols = Math.max(...values.map((row) => row.length), 1);
      const currentColCount = Math.max(
        ...workingRows.map((row) => row.length),
        workingRows[0]?.length ?? 0,
        colIndex + maxPasteCols,
        1,
      );
      const requiredColCount = Math.max(currentColCount, colIndex + maxPasteCols);

      if (workingRows.length === 0 && hasHdr) {
        workingRows.push(Array.from({ length: requiredColCount }, (_, i) => `Column ${i + 1}`));
      } else if (hasHdr && !workingRows[0]) {
        workingRows[0] = Array.from({ length: requiredColCount }, (_, i) => `Column ${i + 1}`);
      }

      for (let r = 0; r < workingRows.length; r += 1) {
        while (workingRows[r].length < requiredColCount) {
          workingRows[r].push(r === 0 && hasHdr ? `Column ${workingRows[r].length + 1}` : '');
        }
      }

      const actualStartRow = hasHdr ? rowIndex + 1 : rowIndex;
      const requiredRowCount = actualStartRow + values.length;
      while (workingRows.length < requiredRowCount) {
        workingRows.push(Array(requiredColCount).fill(''));
      }

      for (let r = 0; r < values.length; r += 1) {
        const targetRowIndex = actualStartRow + r;
        const targetRow = workingRows[targetRowIndex] ?? [];
        if (!workingRows[targetRowIndex]) {
          workingRows[targetRowIndex] = targetRow;
        }
        while (targetRow.length < requiredColCount) {
          targetRow.push('');
        }
        for (let c = 0; c < values[r].length; c += 1) {
          targetRow[colIndex + c] = values[r][c] ?? '';
        }
      }

      applyChange('paste', Papa.unparse(workingRows, { delimiter }));
    }, [input, fmt, hasHdr, applyChange]);

    return (
      <TabularTableView
        input={input}
        format={(config.format as TabularFormat) ?? 'auto'}
        hasHeader={hasHdr}
        trimWhitespace={(config.trimWhitespace as boolean) ?? true}
        editMode={editMode}
        onCellEdit={editMode && onEdit ? handleCellEdit : undefined}
        onAddRow={editMode && onEdit ? handleAddRow : undefined}
        onAddCol={editMode && onEdit ? handleAddCol : undefined}
        onFormatChange={onConfigChange ? handleFormatChange : undefined}
        onDeleteRow={editMode && onEdit ? handleDeleteRow : undefined}
        onDeleteCol={editMode && onEdit ? handleDeleteCol : undefined}
        onHeaderEdit={editMode && onEdit && hasHdr ? handleHeaderEdit : undefined}
        onConvertFormat={editMode && onEdit ? handleConvertFormat : undefined}
        onSort={editMode && onEdit ? handleSort : undefined}
        onPasteCells={editMode && onEdit ? handlePasteCells : undefined}
        onUndo={editMode && onEdit ? undo : undefined}
        onRedo={editMode && onEdit ? redo : undefined}
        canUndo={canUndo}
        canRedo={canRedo}
      />
    );
  }
}

export const TabularDataVisualizerTool = new TabularDataVisualizerToolImpl();
